/**
 * Settings-aware time/date formatting.
 *
 * Mirrors `apps/pos/lib/models/restaurant/restaurant_settings_model.dart`'s
 * `timeFormatPattern` and `shortDatePattern` getters so a timestamp rendered
 * on management web matches the same Date rendered on a POS receipt.
 *
 * Pure functions. No React. Tree-shakable. For React components, use
 * `useTimeFormatter()` from `../hooks/useTimeFormatter` instead — it pulls
 * `Restaurant.settings` from the live `SelectionContext` subscription so
 * settings changes propagate without prop-drilling.
 */

import type { ClockFormat, DateFormatPreset } from '@order-up/models';

export interface TimeFormatSettings {
    clockFormat?: ClockFormat;
    dateFormat?: DateFormatPreset;
    showSeconds?: boolean;
}

const DEFAULT_CLOCK_FORMAT: ClockFormat = '12h';
const DEFAULT_DATE_FORMAT: DateFormatPreset = 'MM-DD-YYYY';

function pad2(n: number): string {
    return n < 10 ? `0${n}` : String(n);
}

/**
 * Time only — respects `clockFormat` + `showSeconds`.
 *
 *   24h + seconds → 23:49:01
 *   24h           → 23:49
 *   12h + seconds → 11:49:01 PM
 *   12h (default) → 11:49 PM
 */
export function formatTime(date: Date, settings?: TimeFormatSettings): string {
    const clockFormat = settings?.clockFormat ?? DEFAULT_CLOCK_FORMAT;
    const showSeconds = settings?.showSeconds ?? false;

    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: clockFormat !== '24h',
    };
    if (showSeconds) {
        options.second = '2-digit';
    }
    // en-US gives `11:49 PM` for hour12=true and `23:49` for hour12=false;
    // both are the canonical project shapes and match POS's `intl` output.
    return new Intl.DateTimeFormat('en-US', options).format(date);
}

/**
 * Date only — respects `dateFormat`. Explicit string assembly (not Intl)
 * so the dash-vs-slash separator is exact across all five presets.
 *
 *   MM-DD-YYYY (default) → 05-19-2026
 *   DD-MM-YYYY           → 19-05-2026
 *   YYYY-MM-DD           → 2026-05-19
 *   MM/DD/YYYY           → 05/19/2026
 *   DD/MM/YYYY           → 19/05/2026
 */
export function formatDate(date: Date, settings?: TimeFormatSettings): string {
    const preset = settings?.dateFormat ?? DEFAULT_DATE_FORMAT;
    const yyyy = String(date.getFullYear());
    const mm = pad2(date.getMonth() + 1);
    const dd = pad2(date.getDate());

    switch (preset) {
        case 'DD-MM-YYYY':
            return `${dd}-${mm}-${yyyy}`;
        case 'YYYY-MM-DD':
            return `${yyyy}-${mm}-${dd}`;
        case 'MM/DD/YYYY':
            return `${mm}/${dd}/${yyyy}`;
        case 'DD/MM/YYYY':
            return `${dd}/${mm}/${yyyy}`;
        case 'MM-DD-YYYY':
        default:
            return `${mm}-${dd}-${yyyy}`;
    }
}

/**
 * `<date>, <time>` joined with comma+space — same shape as `toLocaleString()`
 * so existing UI spacing isn't disrupted when sites swap over.
 */
export function formatDateTime(date: Date, settings?: TimeFormatSettings): string {
    return `${formatDate(date, settings)}, ${formatTime(date, settings)}`;
}
